import { Alert, Box, Button, Container, FormControl, FormControlLabel, FormLabel, Paper, Radio, RadioGroup, TextField, Toolbar, Typography } from '@mui/material';
import { getAuth } from 'firebase/auth';
import * as React from 'react';
import ConfirmationDialog from '../../components/general/ConfirmationDialog';
import { sendGlobalPush, sendPushToUser } from '../../services/user';

type SendTarget = 'USER' | 'ALL';

export default function PushNotificationsPage() {
    const [sendTarget, setSendTarget] = React.useState<SendTarget>('USER');
    const [userId, setUserId] = React.useState('');
    const [title, setTitle] = React.useState('');
    const [body, setBody] = React.useState('');
    const [globalTitle, setGlobalTitle] = React.useState('');
    const [globalMessage, setGlobalMessage] = React.useState('');
    const [eventId, setEventId] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState('');
    const [errorMessage, setErrorMessage] = React.useState('');
    const [confirmGlobalOpen, setConfirmGlobalOpen] = React.useState(false);

    const canSubmit = sendTarget === 'USER'
        ? Boolean(userId.trim() && title.trim() && body.trim())
        : Boolean(globalTitle.trim() && globalMessage.trim());

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (sendTarget === 'ALL') {
            setConfirmGlobalOpen(true);
            return;
        }

        await sendUserPush();
    };

    const sendUserPush = async () => {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setErrorMessage('Not authenticated.');
            return;
        }

        try {
            setIsSubmitting(true);
            const token = await currentUser.getIdToken();
            const response = await sendPushToUser({ userId, title, body, token });
            if (response?.status === 'OK') {
                setSuccessMessage('Push notification queued successfully.');
                setUserId('');
                setTitle('');
                setBody('');
            } else {
                setErrorMessage('Failed to queue push notification.');
            }
        } catch (error: any) {
            setErrorMessage(error?.message ?? 'Failed to queue push notification.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const confirmGlobalPush = async () => {
        setConfirmGlobalOpen(false);
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) {
            setErrorMessage('Not authenticated.');
            return;
        }

        try {
            setIsSubmitting(true);
            const token = await currentUser.getIdToken();
            const response = await sendGlobalPush({ title: globalTitle, message: globalMessage, eventId: eventId || undefined, token });
            if (response?.status === 'OK') {
                setSuccessMessage('Global push notification queued successfully.');
                setGlobalTitle('');
                setGlobalMessage('');
                setEventId('');
            } else {
                setErrorMessage('Failed to queue global push notification.');
            }
        } catch (error: any) {
            setErrorMessage(error?.message ?? 'Failed to queue global push notification.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            component="main"
            sx={{
                backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                        ? theme.palette.grey[100]
                        : theme.palette.grey[900],
                flexGrow: 1,
                height: '100vh',
                overflow: 'auto',
            }}
        >
            <Toolbar />
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Push Notifications
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                        Send a push notification to a specific user or trigger a global push for all users.
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <FormControl sx={{ mb: 3 }}>
                            <FormLabel>Target</FormLabel>
                            <RadioGroup
                                row
                                value={sendTarget}
                                onChange={(event) => {
                                    const nextTarget = event.target.value as SendTarget;
                                    setSendTarget(nextTarget);
                                    setErrorMessage('');
                                    setSuccessMessage('');
                                    if (nextTarget === 'USER') {
                                        setGlobalTitle('');
                                        setGlobalMessage('');
                                        setEventId('');
                                    } else {
                                        setUserId('');
                                        setTitle('');
                                        setBody('');
                                    }
                                }}
                            >
                                <FormControlLabel value="USER" control={<Radio />} label="Specific user" />
                                <FormControlLabel value="ALL" control={<Radio />} label="All users" />
                            </RadioGroup>
                        </FormControl>

                        {sendTarget === 'USER' ? (
                            <>
                                <TextField
                                    fullWidth
                                    label="User ID"
                                    placeholder="Firebase UID"
                                    value={userId}
                                    onChange={(event) => setUserId(event.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Title"
                                    value={title}
                                    onChange={(event) => setTitle(event.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Body"
                                    value={body}
                                    onChange={(event) => setBody(event.target.value)}
                                    multiline
                                    minRows={3}
                                    sx={{ mb: 2 }}
                                />
                            </>
                        ) : (
                            <>
                                <TextField
                                    fullWidth
                                    label="Global title"
                                    value={globalTitle}
                                    onChange={(event) => setGlobalTitle(event.target.value)}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Global message"
                                    value={globalMessage}
                                    onChange={(event) => setGlobalMessage(event.target.value)}
                                    multiline
                                    minRows={3}
                                    sx={{ mb: 2 }}
                                />
                                <TextField
                                    fullWidth
                                    label="Event ID (optional)"
                                    value={eventId}
                                    onChange={(event) => setEventId(event.target.value)}
                                    sx={{ mb: 2 }}
                                />
                            </>
                        )}

                        {successMessage && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {successMessage}
                            </Alert>
                        )}
                        {errorMessage && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {errorMessage}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={!canSubmit || isSubmitting}
                        >
                            {isSubmitting
                                ? 'Sending...'
                                : sendTarget === 'USER'
                                    ? 'Send push notification'
                                    : 'Send global push'}
                        </Button>
                    </form>
                </Paper>
            </Container>
            <ConfirmationDialog
                open={confirmGlobalOpen}
                onClose={() => setConfirmGlobalOpen(false)}
                onConfirm={confirmGlobalPush}
                title="Send global push?"
                message="This will send a push notification to all users. Do you want to proceed?"
            />
        </Box>
    );
}
